/* -------------------------- Design imports start -------------------------- */
import { Box, Card, IconButton, Modal, Slider, Stack, Typography } from '@mui/material'
import ImageEditor from './ImageEditor'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Button from '../Button'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextInput from '../inputs/TextInput'
import FieldLabel from '../widgets/FieldLabel'
import NumberInput from '../inputs/NumberInput'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  filename: string
  confirmButtonProps: {
    text?: string
    onConfirm: (filename: string, quality: number) => Promise<void> | void
    disabled: boolean
  }
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ConfirmImageExportModal(props: Props): ReactElement {
  /* ------------------------- Non state data start ------------------------- */
  const { open, setOpen, filename, confirmButtonProps } = props
  const { t } = useTranslation()
  /* -------------------------- Non state data end -------------------------- */

  /* --------------------------- Flag states start -------------------------- */

  /* ---------------------------- Flag states end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  const [filenameInput, setFilenameInput] = useState(filename)
  const [qualityInput, setQualtityInput] = useState(90)
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------- Callback functions start ----------------------- */
  const handleClose = (): void => {
    // reset modal states
    setQualtityInput(90)
    setOpen(false)
  }
  const handleConfirm = async (): Promise<void> => {
    await confirmButtonProps.onConfirm(filenameInput, qualityInput)
    setOpen(false)
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------------------------------------------------------ */
  /*                             Render component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 'min-content',
          width: '30vw',
          outline: 'none',
        }}
      >
        <Box
          id="export-modal-content-container"
          display="flex"
          flexDirection="column"
          height="100%"
        >
          <Box
            id="export-modal-header"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="16px"
          >
            <Typography variant="h6">{t('common:interaction.button.saveChanges')}</Typography>
            <IconButton onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <Box
            id="export-modal-content"
            flexGrow={1}
            padding="16px"
            sx={{
              overflowY: 'auto',
            }}
          >
            <Stack direction="column" spacing={2}>
              <TextInput
                label={t('common:content.label.fileName')}
                value={filenameInput}
                onChange={input => setFilenameInput(input)}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Slider
                  min={25}
                  max={100}
                  step={5}
                  value={qualityInput}
                  onChange={(_e, value: number | number[]) =>
                    typeof value === 'number' && setQualtityInput(value)
                  }
                />
                <Box width="8rem">
                  <NumberInput
                    label={t('common:content.label.quality')}
                    value={qualityInput}
                    limits={{
                      minInput: 25,
                      maxInput: 101,
                    }}
                    onChange={(input: string | number) =>
                      typeof input === 'number' && setQualtityInput(input)
                    }
                  />
                </Box>
              </Stack>
            </Stack>
          </Box>
          <Box
            id="export-modal-actions"
            display="flex"
            justifyContent="space-between"
            padding="16px"
          >
            <Button variant="contained" color="inherit" onClick={handleClose}>
              {t('common:interaction.button.cancel')}
            </Button>
            <Button variant="contained" onClick={handleConfirm}>
              {confirmButtonProps.text || t('imageManipulation:interaction.button.applyChanges')}
            </Button>
          </Box>
        </Box>
      </Card>
    </Modal>
  )
}
